import { useCallback, useState } from 'react';
import type { Rect } from './geometry';
import { clampToViewport, defaultRect } from './geometry';

function storageKey(kind: string): string {
  return `fw:${kind}`;
}

function viewportNow() {
  return { width: window.innerWidth, height: window.innerHeight };
}

function readStored(kind: string): Rect | null {
  try {
    const raw = localStorage.getItem(storageKey(kind));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as Rect).x === 'number' &&
      typeof (parsed as Rect).y === 'number' &&
      typeof (parsed as Rect).w === 'number' &&
      typeof (parsed as Rect).h === 'number'
    ) {
      return parsed as Rect;
    }
  } catch {
    // private mode / cleared storage — fall through to default
  }
  return null;
}

/** Remembers a window's size/position per `kind` (document type, payment, category, …), клэмп в
 *  текущий viewport при открытии (§1). */
export function useWindowGeometry(kind: string, kindIndex = 0) {
  const [rect, setRectState] = useState<Rect>(() => {
    const stored = readStored(kind);
    return clampToViewport(stored ?? defaultRect(viewportNow(), kindIndex), viewportNow());
  });

  const setRect = useCallback(
    (next: Rect) => {
      setRectState(next);
      try {
        localStorage.setItem(storageKey(kind), JSON.stringify(next));
      } catch {
        // ignore — per-viewer convenience only
      }
    },
    [kind],
  );

  return { rect, setRect };
}
